import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button, MessageBarActions, MessageBarBody, MessageBarTitle, Spinner, useToastController } from '@fluentui/react-components';
import { ReceiptMoney20Regular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { GuideMessageBar } from '../../../theme/components/GuideMessageBar';
import { GuideBannerAction } from '../../../theme/components/GuideButtons';
import { StatusPill } from '../../../theme/components/StatusPill';
import { AppToast, APP_TOASTER_ID } from '../../../commons/component/AppToast/AppToast.Component';
import SettlementFormComponent from '../../Settlements/SettlementForm/Settlement.Form.Component';
import { formatCurrencyAmount } from '../../../commons/currency/Currency.Functions';
import { initialProviderConsumptionsFilter } from '../../../functions/ProviderConsumptions.Functions';
import { getApiErrorMessage } from '../../../functions/ProviderConsumptionImport.Functions';
import {
  ProviderConsumptionsFilter,
  ProviderConsumptionStatus,
  ProviderConsumptionTotals,
} from '../../../models/Entities/ProviderConsumptions/ProviderConsumptions';
import { Settlement } from '../../../models/Entities/Settlements/Settlement';

export interface ProviderToSettleBarProps {
  /** Provider.id (int). */
  providerId: number;
  /** Provider.person.personIdentifier (Guid): sin él no se puede generar. */
  providerIdentifier?: string;
  /** Razón social del proveedor. */
  providerName: string;
  /** CUIT / documento (contexto del alta). */
  providerTaxId?: string;
  /** Cambia con cada recarga de la grilla (marcar, quitar, sincronizar, importar, deshacer): vuelve a pedir los totales. */
  refreshKey: number;
  /** GET providers/{int}/consumptions/totals (thunk de la grilla). */
  LoadTotals: (providerId: number, filter: ProviderConsumptionsFilter) => Promise<ProviderConsumptionTotals>;
}

/** Totales de lo marcado: sólo el estado "Por liquidar", sin los filtros de la grilla (DEC-APP-002). */
export const toSettleTotalsFilter = (): ProviderConsumptionsFilter => ({
  ...initialProviderConsumptionsFilter([]),
  status: ProviderConsumptionStatus.ToSettle,
});

/**
 * Barra "Por liquidar" del proveedor (spec 010, US2): cantidad y total por moneda de lo marcado, y "Generar liquidación"
 * (alta tipo 4 con el formulario de liquidaciones en modo proveedor) que navega al detalle de la liquidación creada.
 */
export const ProviderToSettleBar = (props: ProviderToSettleBarProps) => {
  const { t } = useTranslation();
  const tr = (path: string, options?: Record<string, unknown>) => t(`providerConsumptions.toSettle.bar.${path}`, options);
  const navigate = useNavigate();
  const { dispatchToast } = useToastController(APP_TOASTER_ID);
  const { providerId, LoadTotals } = props;

  const [totals, setTotals] = useState<ProviderConsumptionTotals | undefined>(undefined);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(undefined);
  const [formOpen, setFormOpen] = useState(false);
  const request = useRef(0);

  const load = useCallback(async () => {
    if (!providerId) return;
    const requestId = ++request.current;
    setLoading(true);
    setError(undefined);
    try {
      const next = await LoadTotals(providerId, toSettleTotalsFilter());
      if (requestId === request.current) setTotals(next);
    } catch (failure: unknown) {
      if (requestId === request.current) setError(failure);
    } finally {
      if (requestId === request.current) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId]);

  useEffect(() => {
    load();
  }, [load, props.refreshKey]);

  // al desmontar (p. ej. al navegar a la liquidación creada) se descarta la respuesta pendiente
  useEffect(
    () => () => {
      request.current += 1;
    },
    []
  );

  const count = totals?.totalCount ?? 0;
  const disabledReason = !props.providerIdentifier ? tr('missingIdentifier') : count === 0 ? tr('emptyReason') : undefined;

  const onCreated = (settlement: Settlement) => {
    setFormOpen(false);
    if (settlement?.id) {
      dispatchToast(<AppToast intent="success" title={t('providerSettlement.created')} data-testid="pcs-settlement-created-toast" />, { intent: 'success' });
      navigate(`/settlements/${settlement.id}`);
    } else {
      dispatchToast(<AppToast intent="success" title={t('providerSettlement.createdNoId')} data-testid="pcs-settlement-created-toast" />, { intent: 'success' });
      load();
    }
  };

  const renderTotals = () => {
    if (!totals && loading) return <Spinner size="tiny" label={tr('loading')} labelPosition="after" data-testid="pcs-to-settle-loading" />;
    if (!totals) return null;
    if (count === 0) {
      return (
        <span className="pcs-secondary" data-testid="pcs-to-settle-empty">
          {tr('empty')}
        </span>
      );
    }
    return (
      <>
        <span className="pcs-to-settle-bar__count" data-testid="pcs-to-settle-count">
          {tr('count', { count })}
        </span>
        <ul className="pcs-to-settle-bar__totals" aria-label={tr('totalsLabel')}>
          {totals.byCurrency.map((currency) => (
            <li key={currency.currencyCode} data-testid={`pcs-to-settle-total-${currency.currencyCode}`}>
              <StatusPill tone="neutral">{currency.currencyCode}</StatusPill>
              <span className="ichtys-num pcs-to-settle-bar__amount">{formatCurrencyAmount(currency.amount, currency.currencyCode)}</span>
            </li>
          ))}
        </ul>
      </>
    );
  };

  return (
    <section className="pcs-to-settle-bar" aria-labelledby="pcs-to-settle-title" aria-busy={loading || undefined} data-testid="pcs-to-settle-bar">
      <div className="pcs-to-settle-bar__info" aria-live="polite">
        <h3 id="pcs-to-settle-title" className="pcs-to-settle-bar__title">
          {tr('title')}
        </h3>
        {renderTotals()}
      </div>
      <div className="pcs-to-settle-bar__actions">
        <Button
          appearance="primary"
          icon={<ReceiptMoney20Regular />}
          disabled={!!disabledReason || loading}
          aria-describedby={disabledReason ? 'pcs-to-settle-reason' : undefined}
          onClick={() => setFormOpen(true)}
          data-testid="pcs-generate-settlement"
        >
          {tr('generate')}
        </Button>
        {disabledReason && totals && (
          <span id="pcs-to-settle-reason" className="pcs-secondary pcs-to-settle-bar__reason" data-testid="pcs-to-settle-reason">
            {disabledReason}
          </span>
        )}
      </div>
      {error !== undefined && (
        <GuideMessageBar intent="warning" layout="multiline" className="pcs-to-settle-bar__error" data-testid="pcs-to-settle-load-error">
          <MessageBarBody>
            <MessageBarTitle>{tr('loadError')}</MessageBarTitle>
            {getApiErrorMessage(error)}
          </MessageBarBody>
          <MessageBarActions>
            <GuideBannerAction onClick={() => load()} data-testid="pcs-to-settle-load-retry">
              {tr('retry')}
            </GuideBannerAction>
          </MessageBarActions>
        </GuideMessageBar>
      )}
      {formOpen && props.providerIdentifier && (
        <SettlementFormComponent
          isOpen={formOpen}
          openPanel={() => setFormOpen(true)}
          dismissPanel={() => setFormOpen(false)}
          Provider={{ id: providerId, personIdentifier: props.providerIdentifier, businessName: props.providerName, taxId: props.providerTaxId }}
          ToSettleTotals={totals}
          onCreated={onCreated}
          onOpenSettlement={(settlementId: number) => navigate(`/settlements/${settlementId}`)}
        />
      )}
    </section>
  );
};

export default ProviderToSettleBar;
